import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../config/constants";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

function getStoredToken() {
  return localStorage.getItem("lane4_access_token");
}

function getStoredRefreshToken() {
  return localStorage.getItem("lane4_refresh_token");
}

function setStoredTokens(access, refresh) {
  if (access) localStorage.setItem("lane4_access_token", access);
  if (refresh) localStorage.setItem("lane4_refresh_token", refresh);
}

function clearStoredTokens() {
  localStorage.removeItem("lane4_access_token");
  localStorage.removeItem("lane4_refresh_token");
}

function authHeader() {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const authAxios = () => {
  return axios.create({ headers: authHeader() });
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const tryRefresh = useCallback(async () => {
    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) return false;
    try {
      const res = await axios.post(`${API}/auth/refresh`, {}, { headers: { "X-Refresh-Token": refreshToken } });
      setStoredTokens(res.data.access_token, null);
      const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${res.data.access_token}` } });
      setUser(me.data);
      return true;
    } catch (err) {
      console.error("Token refresh failed:", err.message);
      return false;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const token = getStoredToken();
    if (!token) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setUser(res.data);
    } catch {
      const refreshed = await tryRefresh();
      if (!refreshed) { clearStoredTokens(); setUser(null); }
    } finally {
      setLoading(false);
    }
  }, [tryRefresh]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    setStoredTokens(res.data.access_token, res.data.refresh_token);
    setUser({ id: res.data.id, email: res.data.email, name: res.data.name, role: res.data.role });
    return res.data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (err) { console.error("Logout request failed:", err.message); }
    clearStoredTokens();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, authHeader, tryRefresh }}>
      {children}
    </AuthContext.Provider>
  );
};
